import { useState, type ReactNode } from 'react'
import { router } from '@inertiajs/react'
import type { RequestPayload } from '@inertiajs/core'

import { FormDialog } from '@components/molecules/FormDialog'
import AsyncSelect from '@molecules/AsyncSelect'
import { MultiSelectField } from '@components/molecules/MultiSelectField'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { SelectField } from '@components/molecules/SelectField'
import { GroupSupporterSearchDocument } from '@frontend/graphql/queries/groupSupporterSearch.generated'
import { useGraphQLClient } from '@utils/GraphQLClientProvider'
import { scopedTranslation } from '@utils/I18n'
import type { SelectOption } from '@utils/types'

const t = scopedTranslation('views.call_lists.add_supporters')
const tValidations = scopedTranslation('shared.validations')

export type AddSupportersOptions = {
  tagFilters: SelectOption[]
  levelsOfEngagement: SelectOption[]
  advancedFilters: SelectOption[]
  events: SelectOption[]
}

type Mode = 'name' | 'filters' | 'advanced_filter' | 'level_of_engagement' | 'rsvps' | 'all_people'
type RsvpType = 'all_rsvps' | 'attendees' | 'non_attendees'

type AddSupportersDialogProps = {
  callListName: string
  groupId: string
  groupName: string
  href: string
  options: AddSupportersOptions
  onClose: () => void
}

const required = (label: string) => ({ type: 'required', message: tValidations('required', { attribute_name: label }) })

export function AddSupportersDialog({
  callListName,
  groupId,
  groupName,
  href,
  options,
  onClose,
}: AddSupportersDialogProps) {
  const client = useGraphQLClient()
  const [mode, setMode] = useState<Mode>('name')
  const [supporter, setSupporter] = useState<SelectOption | null>(null)
  const [tagFilterIds, setTagFilterIds] = useState<string[]>([])
  const [levelIds, setLevelIds] = useState<string[]>([])
  const [advancedFilterId, setAdvancedFilterId] = useState<string | null>(null)
  const [eventId, setEventId] = useState<string | null>(null)
  const [rsvpType, setRsvpType] = useState<RsvpType>('all_rsvps')
  const [busy, setBusy] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const searchSupporters = async (fullNameICont: string) => {
    if (fullNameICont.trim() === '') return []
    const data = await client.request(GroupSupporterSearchDocument, { groupId, fullNameICont })
    return (data.group?.supporters.nodes ?? []).flatMap((node) =>
      node ? [{ value: node.id, label: [node.fullName, node.email].filter(Boolean).join(' · ') }] : []
    )
  }

  const params: Record<Mode, { missing: boolean; data: RequestPayload }> = {
    name: { missing: !supporter, data: { person: { add_by: 'name', person_id: supporter?.value ?? null } } },
    filters: {
      missing: tagFilterIds.length === 0,
      data: { person: { add_by: 'filters' }, non_exclusive_filter: tagFilterIds },
    },
    advanced_filter: {
      missing: !advancedFilterId,
      data: { person: { add_by: 'advanced_filter' }, advanced_filter_id: advancedFilterId },
    },
    level_of_engagement: {
      missing: levelIds.length === 0,
      data: { person: { add_by: 'level_of_engagement' }, exclusive_filter: levelIds },
    },
    rsvps: { missing: !eventId, data: { person: { add_by: 'rsvps' }, event: { id: eventId, rsvp_type: rsvpType } } },
    all_people: { missing: false, data: { person: { add_by: 'all_people' } } },
  }

  const errorFor = (field: string, label: string, missing: boolean) =>
    showErrors && missing ? { [field]: required(label) } : undefined

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (params[mode].missing) {
      setShowErrors(true)
      return
    }
    router.post(href, params[mode].data, {
      preserveScroll: true,
      onStart: () => setBusy(true),
      onFinish: () => setBusy(false),
      onSuccess: onClose,
    })
  }

  const multiSelect = (
    name: string,
    label: string,
    choices: SelectOption[],
    value: string[],
    onValueChange: (value: string[]) => void
  ) => (
    <MultiSelectField
      name={name}
      label={label}
      required
      options={choices}
      value={value}
      onValueChange={onValueChange}
      placeholder={t('placeholders.choose')}
      searchPlaceholder={t('placeholders.search')}
      noResultsText={t('no_results')}
      errors={errorFor(name, label, value.length === 0)}
    />
  )

  const modes: Array<{ value: Mode; label: string; whenSelected?: ReactNode; available?: boolean }> = [
    {
      value: 'name',
      label: t('modes.name'),
      whenSelected: (
        <AsyncSelect
          name="person_id"
          displayName={t('labels.supporter')}
          loadOptions={searchSupporters}
          value={supporter ?? undefined}
          onChange={(option) => setSupporter(option as SelectOption | null)}
          isMulti={false}
          isCreatable={false}
          placeholder={t('placeholders.supporter')}
          menuPortalTarget={document.body}
          errors={errorFor('person_id', t('labels.supporter'), !supporter)}
        />
      ),
    },
    {
      value: 'filters',
      label: t('modes.filters'),
      available: options.tagFilters.length > 0,
      whenSelected: multiSelect(
        'non_exclusive_filter',
        t('modes.filters'),
        options.tagFilters,
        tagFilterIds,
        setTagFilterIds
      ),
    },
    {
      value: 'advanced_filter',
      label: t('modes.advanced_filter'),
      available: options.advancedFilters.length > 0,
      whenSelected: (
        <SelectField
          name="advanced_filter_id"
          label={t('modes.advanced_filter')}
          required
          search
          options={options.advancedFilters}
          value={advancedFilterId}
          onValueChange={setAdvancedFilterId}
          placeholder={t('placeholders.choose')}
          errors={errorFor('advanced_filter_id', t('modes.advanced_filter'), !advancedFilterId)}
        />
      ),
    },
    {
      value: 'level_of_engagement',
      label: t('modes.level_of_engagement'),
      available: options.levelsOfEngagement.length > 0,
      whenSelected: multiSelect(
        'exclusive_filter',
        t('modes.level_of_engagement'),
        options.levelsOfEngagement,
        levelIds,
        setLevelIds
      ),
    },
    {
      value: 'rsvps',
      label: t('modes.rsvps'),
      available: options.events.length > 0,
      whenSelected: (
        <div className="tw:flex tw:flex-col tw:gap-4">
          <SelectField
            name="event_id"
            label={t('labels.event')}
            required
            search
            options={options.events}
            value={eventId}
            onValueChange={setEventId}
            placeholder={t('placeholders.choose')}
            errors={errorFor('event_id', t('labels.event'), !eventId)}
          />
          <RadioGroupField<RsvpType>
            label={t('labels.rsvp_type')}
            value={rsvpType}
            onValueChange={setRsvpType}
            options={(['all_rsvps', 'attendees', 'non_attendees'] as const).map((value) => ({
              value,
              label: t(`rsvp_types.${value}`),
            }))}
          />
        </div>
      ),
    },
    { value: 'all_people', label: t('modes.all_people', { group: groupName }) },
  ]

  return (
    <FormDialog.Root open onOpenChange={(open) => !open && onClose()} size="lg" position="top">
      <FormDialog.Title>{t('title', { name: callListName })}</FormDialog.Title>
      <FormDialog.Form onSubmit={submit}>
        <FormDialog.Body>
          <RadioGroupField<Mode>
            label={t('add_by')}
            value={mode}
            onValueChange={setMode}
            options={modes
              .filter((option) => option.available !== false)
              .map((option) => ({
                ...option,
                whenSelected: option.whenSelected && <div className="tw:my-2 tw:ml-6">{option.whenSelected}</div>,
              }))}
          />
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('submit')} isLoading={busy} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
